import { API_BASE_URL } from '@/config/api'
import { getStoredToken } from '@/services/auth'
import type { FactoryBaoLocCell } from '@/store/api/factoryBaoLocApi'

interface DownloadFactoryBaoLocExportArgs {
  filtered: boolean
  startDate: string
  endDate: string
  rows: Array<Record<string, FactoryBaoLocCell>>
}

function extractFilename(disposition: string | null, fallback: string) {
  if (!disposition) return fallback
  const match = /filename="?([^";]+)"?/i.exec(disposition)
  return match?.[1] || fallback
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function csvEscape(value: FactoryBaoLocCell) {
  const text = value === null || value === undefined ? '' : String(value)
  return `"${text.replace(/"/g, '""')}"`
}

function buildCsv(rows: Array<Record<string, FactoryBaoLocCell>>) {
  if (!rows.length) return 'message\n"No rows in current view"\n'
  const fields = Object.keys(rows[0]).filter((field) => field !== 'id')
  const lines = [
    fields.join(','),
    ...rows.map((row) => fields.map((field) => csvEscape(row[field])).join(',')),
  ]
  return `${lines.join('\n')}\n`
}

export async function downloadFactoryBaoLocExport({
  filtered,
  startDate,
  endDate,
  rows,
}: DownloadFactoryBaoLocExportArgs) {
  const params = filtered ? `?${new URLSearchParams({ start_date: startDate, end_date: endDate }).toString()}` : ''
  const endpoint = filtered ? 'export-filtered' : 'export'
  const fallbackName = filtered
    ? `factory_bao_loc_${startDate}_${endDate}.csv`
    : 'factory_bao_loc_all.csv'

  try {
    const token = getStoredToken()
    const response = await fetch(`${API_BASE_URL}/factories/bao-loc/${endpoint}${params}`, {
      headers: token ? { authorization: `Bearer ${token}` } : undefined,
    })
    if (!response.ok) throw new Error(`Export failed with ${response.status}`)

    const blob = await response.blob()
    const filename = extractFilename(response.headers.get('Content-Disposition'), fallbackName)
    downloadBlob(blob, filename)
  } catch {
    downloadBlob(new Blob([buildCsv(rows)], { type: 'text/csv;charset=utf-8' }), fallbackName)
  }
}
